import { get, random } from 'lodash';

import { PatientFormTemplateItem } from '@lib/resources/types';

export const getLumaBotItemPatientInformation = (): PatientFormTemplateItem => {
  const id = `patient-information-${random(99999)}`;
  const idFirstName = `${id}-patient.firstName`;
  const idLastName = `${id}-patient.lastName`;
  const idPhone = `${id}-patient.phone`;
  const idDateOfBirth = `${id}-patient.dob`;
  const id2FA = `${id}-2fa`;
  const idEnd = `${id}-end`;

  return {
    id,
    type: 'patient_information',
    squiggly: id,
    title: '',
    redirectType: 'none',
    properties: {
      visibility: 'default',
      showDescription: false,
      items: [
        {
          id: `${id}-jump-end`,
          type: 'jump',
          squiggly: 'jump-end',
          title: '',
          redirectType: 'none',
          properties: {
            showDescription: false,
            script: "let jump\nif (context.get('patient')) jump = 'skipAll';\njump",
            answers: [
              {
                id: `${id}-answer-skipAll`,
                redirectType: 'goTo',
                label: 'skipAll',
                properties: {
                  goTo: idEnd,
                },
              },
            ],
          },
          validations: {
            required: false,
          },
        },
        {
          id: `${id}-introduction`,
          type: 'template',
          title: 'Great, I can help you with that. I need some information to get you to the right place.',
          redirectType: 'none',
          properties: {
            template: '',
          },
          validations: {},
        },
        {
          id: `${id}-jump-skipFirstName`,
          type: 'jump',
          squiggly: 'jump-skipFirstName',
          title: '',
          redirectType: 'none',
          properties: {
            showDescription: false,
            script:
              "let jump\nif (context.get('patientForm').answers['patient.firstName']) jump = 'skipFirstName';\njump",
            answers: [
              {
                id: `${id}-answer-skipFirstName`,
                redirectType: 'goTo',
                label: 'skipFirstName',
                properties: {
                  goTo: idLastName,
                },
              },
            ],
          },
          validations: {},
        },
        {
          id: idFirstName,
          type: 'short_text',
          squiggly: 'patient.firstName',
          title: "What's your first name?",
          redirectType: 'none',
          properties: {
            visibility: 'default',
            mustExist: true,
          },
          validations: {
            required: true,
          },
        },
        {
          id: `${id}-jump-skipLastName`,
          type: 'jump',
          squiggly: 'jump-skipLastName',
          title: '',
          redirectType: 'none',
          properties: {
            showDescription: false,
            script:
              "let jump\nif (context.get('patientForm').answers['patient.lastName']) jump = 'skipLastName';\njump",
            answers: [
              {
                id: `${id}-answer-skipLastName`,
                redirectType: 'goTo',
                label: 'skipLastName',
                properties: {
                  goTo: idDateOfBirth,
                },
              },
            ],
          },
          validations: {},
        },
        {
          id: idLastName,
          type: 'short_text',
          squiggly: 'patient.lastName',
          title: "Hello {{patient.firstName}}. What's your last name?",
          redirectType: 'none',
          properties: {
            visibility: 'default',
            mustExist: true,
          },
          validations: {
            required: true,
          },
        },
        {
          id: `${id}-jump-skipDateOfBirth`,
          type: 'jump',
          squiggly: 'jump-skipDateOfBirth',
          title: '',
          redirectType: 'none',
          properties: {
            showDescription: false,
            script: "let jump\nif (context.get('patientForm').answers['patient.dob']) jump = 'skipDateOfBirth';\njump",
            answers: [
              {
                id: `${id}-answer-skipDateOfBirth`,
                redirectType: 'goTo',
                label: 'skipDateOfBirth',
                properties: {
                  goTo: idPhone,
                },
              },
            ],
          },
          validations: {},
        },
        {
          id: idDateOfBirth,
          type: 'date',
          squiggly: 'patient.dob',
          title: 'When is your birth date?',
          redirectType: 'none',
          properties: {
            visibility: 'default',
            mustExist: true,
          },
          validations: {
            required: true,
          },
        },
        {
          id: `${id}-jump-skipPhone`,
          type: 'jump',
          squiggly: 'jump-skipPhone',
          title: '',
          redirectType: 'none',
          properties: {
            showDescription: false,
            script: "let jump\nif (context.get('patientForm').answers['patient.phone']) jump = 'skipPhone';\njump",
            answers: [
              {
                id: `${id}-answer-skipPhone`,
                redirectType: 'goTo',
                label: 'skipPhone',
                properties: {
                  goTo: id2FA,
                },
              },
            ],
          },
          validations: {},
        },
        {
          id: idPhone,
          type: 'phone_number',
          squiggly: 'patient.phone',
          title: "What's your phone number? This is where I'll send you a verification code.",
          redirectType: 'none',
          properties: {
            visibility: 'default',
            mustExist: true,
          },
          validations: {
            required: true,
          },
        },
        {
          id: id2FA,
          type: '2FA',
          squiggly: '2fa',
          title:
            "When you click Send Code, you'll receive a text message if you entered a mobile number. If you entered a landline number, you'll receive a phone call.",
          redirectType: 'none',
          properties: {
            showDescription: false,
            visibility: 'default',
            showCustomAction: false,
            scoringEnabled: false,
            addToAppointment: false,
          },
          validations: {
            required: true,
          },
        },
        // Additional item so we can redirect here in case the patient already exists
        {
          id: idEnd,
          type: 'template',
          squiggly: 'patient-information-end',
          title: '',
          redirectType: 'none',
          properties: {
            template: '',
          },
          validations: {},
        },
      ],
    },
    validations: {
      required: false,
    },
  };
};

const lumaBotNotifyStaffPrefix = 'New LumaBot notification:notifyStaff:';

export const getLumaBotNotifyStaffMessageText = (flowId: string, itemId: string) =>
  `${lumaBotNotifyStaffPrefix}${flowId}:${itemId}`;

export const getLumaBotNotifyStaffMessageMetadata = (text: string) => {
  const matches = text.match(new RegExp(`^${lumaBotNotifyStaffPrefix}(.+):(.+)$`));
  return { flowId: get(matches, 1), item: get(matches, 2) };
};

export const getLumaBotNotifyStaffMessageMetadataV2 = (text: string) => {
  const lumaBotNotifyStaffPrefixV2 = 'lumabot:sendToHub:';
  const lumaBotNotifyStaffPrefixV2Item = ':item-';
  const matches = text.match(new RegExp(`^${lumaBotNotifyStaffPrefixV2}(.{24})${lumaBotNotifyStaffPrefixV2Item}(.+)$`));
  return { flowId: get(matches, 1), item: get(matches, 2) };
};
